import { Icon, Text, useToast } from '@cad-arc';
import classnames from 'classnames';
import { parseJSON } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import { AdditionalInfoType, AdditionalInfoView, Priority } from '../types';
import { formatAdditionalInfoTime, isProQaInitOrEndMessage } from './additional-info-row';
import { FlagRenderer } from '../../common/components/flag-renderer/flag-renderer';
import { NarrativeActionMenu } from './narrative-action-menu';
import { NarrativeEditRow } from './narrative-edit-row';
import { DeleteNarrativeConfirmation } from './delete-narrative-confirmation';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';
import { useNarrativeRowActions } from '../hooks/use-narrative-row-actions/use-narrative-row-actions';
import { NarrativeRowNavProps } from '../hooks/use-narrative-keyboard-nav/use-narrative-keyboard-nav';
import { useNarrativeRowKeyboard } from '../hooks/use-narrative-row-keyboard/use-narrative-row-keyboard';
import styles from './pinned-narrative-row.module.css';

export const PinnedNarrativeRow = observer(function PinnedNarrativeRow({
    data: info,
    isFirst,
    isLast,
    currentUserId,
    agencyEventNumberToIdMap,
    isScheduledEvent = false,
    isBolo = false,
    isFirstResponder = false,
    isReadOnlyMode = false,
    onOpenEvent,
    onScrollTo,
    focusNarrativeInput,
    isPrevSiblingHighPriority = false,
    isNextSiblingHighPriority = false,
    rowNavProps,
}: {
    data: AdditionalInfoView;
    isFirst: boolean;
    isLast: boolean;
    currentUserId?: number;
    agencyEventNumberToIdMap?: Record<string, number>;
    isScheduledEvent?: boolean;
    isBolo?: boolean;
    isFirstResponder?: boolean;
    isReadOnlyMode?: boolean;
    onOpenEvent?: (id: number) => void;
    /** Called when the "Scroll to" (Crosshairs) icon in NarrativeActionMenu is clicked.
     *  AdditionalInfoRenderer uses this to invoke reanchorAtMessage(info.id) and scroll
     *  to the chronological position of the pinned entry. */
    onScrollTo?: (info: AdditionalInfoView) => void;
    focusNarrativeInput?: () => void;
    /** Whether the pinned row immediately before/after this one (in pin order) is
     *  also HIGH priority. Used (alongside isFirst/isLast) to compute squareTop/
     *  squareBottom — see the .square-top/.square-bottom comment in
     *  pinned-narrative-row.module.css for the corner-rounding model, which
     *  mirrors additional-info-row.module.css's chronological-list rows. */
    isPrevSiblingHighPriority?: boolean;
    isNextSiblingHighPriority?: boolean;
    /** Roving-tabindex + arrow-key nav wiring for the pinned group. Omitted when
     *  keyboard navigation is disabled, leaving the row non-focusable as before. */
    rowNavProps?: NarrativeRowNavProps;
}): React.JSX.Element {
    const { entityId, entityNumber } = useAdditionalInfos();
    const toast = useToast();

    const isUrgent = info.priority === Priority.HIGH;
    const isProQAInitOrEndMsg = isProQaInitOrEndMessage(info);
    const infoTypeSystem = info.type === AdditionalInfoType.SYSTEM;
    const isEmptyText = !info.text?.trim();
    const isOnly = isFirst && isLast;
    const squareTop = !isFirst && (!isUrgent || isPrevSiblingHighPriority);
    const squareBottom = !isLast && (!isUrgent || isNextSiblingHighPriority);
    const isMergedEventNarrative =
        info.parentAgencyEventId != null && info.parentAgencyEventId === entityId;

    // PinnedNarrativeRow always runs the success side effects (toast + focus),
    // unlike AdditionalInfoRow which gates them behind the narrative-filtering flag.
    const handleDeleteSuccess = useCallback(() => {
        toast({ status: 'default', description: 'Narrative Message Deleted' });
        focusNarrativeInput?.();
    }, [toast, focusNarrativeInput]);
    // Pinned rows surface delete failures via an inline toast (not showFailedToDeleteAlert).
    const handleDeleteError = useCallback(() => {
        toast({
            status: 'default',
            description: `Failed to delete narrative message for event ${entityNumber}.`,
        });
    }, [toast, entityNumber]);

    const handleOpenMergedEvent = useCallback(() => {
        onOpenEvent?.(info.agencyEventId);
    }, [onOpenEvent, info.agencyEventId]);

    // handleToggleEmphasis (from useNarrativeRowActions) must NOT reorder the
    // pinned section (pinned order is by pin datetime only); the provider's
    // updatePinnedEntry patches priority in place so styling toggles while
    // position + pin icon stay put.
    const {
        isEditing,
        isSaving,
        handleEdit,
        handleEditCancel,
        handleEditSave,
        isTogglingEmphasis,
        handleToggleEmphasis,
        isDeleting,
        isDeleteConfirmOpen,
        executeDelete,
        handleDelete,
        handleDeleteCancel,
        handleUnpin,
        isPinning,
        canShowActionMenu,
        isSelected,
        handleRowFocus,
        handleRowBlur,
        handleRowDeselect,
        handleRowMouseDown,
    } = useNarrativeRowActions({
        info,
        isPinnedRow: true,
        returnFocusToRow: rowNavProps?.focusSelf,
        // Pinned rows always confirm before delete (no legacy direct-delete path).
        shouldConfirmBeforeDelete: true,
        onDeleteSuccess: handleDeleteSuccess,
        onDeleteError: handleDeleteError,
    });

    const { handleMenuEscape, handleRowKeyDown, handleFocus, handleBlur } = useNarrativeRowKeyboard(
        {
            rowNavProps,
            handleRowDeselect,
            handleRowFocus,
            handleRowBlur,
            focusNarrativeInput,
        }
    );

    if (isDeleteConfirmOpen) {
        return (
            <DeleteNarrativeConfirmation
                messageText={info.text}
                isLoading={isDeleting}
                isFirstResponder={isFirstResponder}
                onConfirm={executeDelete}
                onClose={handleDeleteCancel}
            />
        );
    }

    return (
        <div
            ref={rowNavProps?.ref}
            tabIndex={rowNavProps?.tabIndex}
            onKeyDown={handleRowKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onMouseDown={handleRowMouseDown}
            onClick={handleRowDeselect}
            className={classnames(styles['pinned-row'], {
                [styles['last']]: isLast && !isOnly,
                [styles['only']]: isOnly,
                [styles['high-priority']]: isUrgent,
                [styles['square-top']]: squareTop,
                [styles['square-bottom']]: squareBottom,
                [styles['lg']]: isFirstResponder,
                [styles['is-empty-text']]: isEmptyText,
                [styles['is-editing']]: isEditing,
                [styles['is-selected']]: isSelected,
            })}
            data-testid="PINNED_NARRATIVE_ROW"
            data-infoid={info.id}
        >
            <div
                className={classnames(styles['left-group'], {
                    [styles['left-group-lg']]: isFirstResponder,
                })}
            >
                <Icon
                    icon="Pin"
                    size={isFirstResponder ? 'md' : 'sm'}
                    className={styles['pin-icon']}
                    data-testid="PINNED_NARRATIVE_ICON"
                />
                <Text
                    as="div"
                    className={classnames(
                        styles['text'],
                        isFirstResponder ? styles['text-lg'] : undefined,
                        {
                            [styles['proqa-init-close']]: isProQAInitOrEndMsg,
                            [styles['system']]: infoTypeSystem,
                            [styles['system-lg']]: infoTypeSystem && isFirstResponder,
                            [styles['system-sm']]: infoTypeSystem && !isFirstResponder,
                        }
                    )}
                    data-testid="PINNED_NARRATIVE_TEXT"
                >
                    {isEditing ? (
                        <NarrativeEditRow
                            info={info}
                            agencyEventId={info.agencyEventId}
                            isFirstResponder={isFirstResponder}
                            onSave={handleEditSave}
                            onCancel={handleEditCancel}
                            isSaving={isSaving}
                        />
                    ) : (
                        <FlagRenderer
                            text={info.text}
                            agencyEventNumberToIdMap={agencyEventNumberToIdMap}
                            isFirstResponder={isFirstResponder}
                            onAgencyEventNumberClick={({ eventId }) => {
                                onOpenEvent?.(eventId);
                            }}
                        />
                    )}
                </Text>
            </div>
            <div className={styles['meta']}>
                <Text
                    as="div"
                    className={classnames(styles['date'], isFirstResponder && styles['date-lg'])}
                >
                    {formatAdditionalInfoTime(parseJSON(info.createdDateUtc), new Date())}
                </Text>
                <Text
                    as="div"
                    className={classnames(
                        styles['author'],
                        isFirstResponder && styles['author-lg']
                    )}
                >
                    {info.authorCallSign || info.userInitials}
                    {isMergedEventNarrative && (
                        <div
                            data-testid="PINNED_NARRATIVE_PARENT_EVENT_LINK"
                            className={styles['parent-event-link']}
                        >
                            #{info.agencyEventNumber.slice(-4)}
                        </div>
                    )}
                </Text>
            </div>
            {canShowActionMenu && (
                <NarrativeActionMenu
                    isPinned
                    info={info}
                    currentUserId={currentUserId}
                    isScheduledEvent={isScheduledEvent}
                    isBolo={isBolo}
                    isFirstResponder={isFirstResponder}
                    isMergedEventNarrative={isMergedEventNarrative}
                    readOnly={isReadOnlyMode}
                    onEdit={handleEdit}
                    onToggleEmphasis={handleToggleEmphasis}
                    onUnpin={handleUnpin}
                    onOpenMergedEvent={handleOpenMergedEvent}
                    onDelete={handleDelete}
                    onScrollTo={onScrollTo ? () => onScrollTo(info) : undefined}
                    isDeleting={isDeleting}
                    isTogglingEmphasis={isTogglingEmphasis}
                    isPinning={isPinning}
                    onActionClick={handleRowDeselect}
                    onEscape={handleMenuEscape}
                />
            )}
        </div>
    );
});
